import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAniList } from '../context/AniListContext';

import { useTimeSettings } from '../context/TimeSettingsContext';
import { useTitlePreference } from '../context/TitlePreferenceContext';
import { useUndoRedo } from '../context/UndoRedoContext';
import { useLinkSettings } from '../context/LinkSettingsContext';
import { useAlert } from '../context/ToastContext';
import { formatDuration, formatEpisodeProgress, getTotalEpisodes, calculateAnimeTime, getMediaStatusBadge } from '../utils/timeFormatter';
import { filterGroupsByStatus, formatReleaseDate } from '../services/groupingService';
import { getDisplayTitle } from '../hooks/useDisplayTitle';
import { updateAnimeStatus, updateAnimeProgress, updateMediaScore } from '../services/aniListService';
import { DashboardSkeleton } from './common/SkeletonLoader';
import StatusButton from './StatusButton';
import EpisodeCounter from './EpisodeCounter';
import SiteLinksModal from './SiteLinksModal';
import ScoreInput from './ScoreInput';

import './Dashboard.css';

const STATUS_TABS = [
    { id: 'ALL', label: 'All', value: null },
    { id: 'CURRENT', label: 'Watching', value: 'CURRENT' },
    { id: 'CONTINUE', label: 'Continue', value: 'CONTINUE' },
    { id: 'PLANNING', label: 'Planning', value: 'PLANNING' },
    { id: 'PAUSED', label: 'On Hold', value: 'PAUSED' },
    { id: 'DROPPED', label: 'Dropped', value: 'DROPPED' },
    { id: 'HALF_COMPLETED', label: 'Half Completed', value: 'HALF_COMPLETED' },
    { id: 'COMPLETED', label: 'Completed', value: 'COMPLETED' }
];

const OVERVIEW_TAB_STORAGE_KEY = 'overview_last_active_tab';
const OVERVIEW_SORT_BY_KEY = 'overview_sort_by';
const OVERVIEW_SORT_DIR_KEY = 'overview_sort_direction';

const SORT_OPTIONS = [
    { id: 'name', label: '📝 Name', sortFn: (a, b, dir) => dir * a.name.localeCompare(b.name) },
    { id: 'averageScore', label: '⭐ Average Score', sortFn: (a, b, dir) => dir * (b.stats.averageScore - a.stats.averageScore) },
    { id: 'totalAnime', label: '📚 Total Anime', sortFn: (a, b, dir) => dir * (b.stats.animeCount - a.stats.animeCount) },
    { id: 'totalDuration', label: '⏱️ Total Duration', sortFn: (a, b, dir) => dir * (b.stats.totalDuration - a.stats.totalDuration) },
    { id: 'watchedTime', label: '✅ Watched Time', sortFn: (a, b, dir) => dir * (b.stats.watchedDuration - a.stats.watchedDuration) },
    { id: 'remainingTime', label: '⏳ Remaining Time', sortFn: (a, b, dir) => dir * ((b.stats.totalDuration - b.stats.watchedDuration) - (a.stats.totalDuration - a.stats.watchedDuration)) }
];

const TIPS = [
    { icon: '📊', text: 'Check "My Stats" for detailed insights like total watch time, completion rates, and score distributions.' },
    { icon: '🔗', text: 'Set up quick links to your favorite streaming sites in Settings → External Site Links.' },
    { icon: '⚡', text: 'Quick Wins shows anime you\'re close to finishing — perfect for a quick binge session!' },
    { icon: '🆚', text: 'Compare your anime tastes with any AniList user using the Compare tab.' },
    { icon: '🎯', text: 'Related Anime finds sequels, prequels, and spin-offs connected to anime you\'ve already watched.' },
    { icon: '🎨', text: 'Go to Settings → Appearance to switch between Fire, Cosmic, and Midnight themes.' },
    { icon: '🔀', text: 'Marathon Mode gives you a guided binge experience — it picks what to watch next!' },
    { icon: '🇯🇵', text: 'Prefer Japanese titles? Switch between English and Romaji in Settings → Display Preferences.' },
    { icon: '🐛', text: 'Found a bug? Report it via Settings → Feedback & Support to help us improve!' },
    { icon: '💡', text: 'Have a feature idea? Submit it through Settings → Feedback & Support — we read every suggestion!' },
    { icon: '⭐', text: 'You can rate anime directly from the group detail modal — no need to visit AniList!' },
    { icon: '📈', text: 'The "Continue" tab shows groups where you have unfinished watching anime — pick up where you left off!' },
    { icon: '🎬', text: 'Click the 🎬 button on any anime to open streaming links for that title.' },
];

const Dashboard = () => {
    const navigate = useNavigate();
    const { animeGroups, isRefreshing, isLoading, userData, refreshData, token } = useAniList();

    const { hoursPerDay } = useTimeSettings();
    const { titlePreference } = useTitlePreference();
    const { addChange } = useUndoRedo();
    const { watchLinks } = useLinkSettings();
    const { showError } = useAlert();

    // Pick a random tip on each mount (page load/refresh)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const randomTip = useMemo(() => TIPS[Math.floor(Math.random() * TIPS.length)], []);

    const [activeTab, setActiveTab] = useState(() => {
        const saved = localStorage.getItem(OVERVIEW_TAB_STORAGE_KEY);
        // Validate that saved value is a valid tab ID
        const isValid = saved && STATUS_TABS.some(tab => tab.id === saved);
        return isValid ? saved : 'ALL';
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState(() => {
        return localStorage.getItem(OVERVIEW_SORT_BY_KEY) || 'averageScore';
    });
    const [sortDirection, setSortDirection] = useState(() => {
        return localStorage.getItem(OVERVIEW_SORT_DIR_KEY) || 'asc';
    });
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [showWatchModal, setShowWatchModal] = useState(false);
    const [selectedAnimeForModal, setSelectedAnimeForModal] = useState('');
    const [optimisticUpdates, setOptimisticUpdates] = useState(new Map());
    const [changingStatus, setChangingStatus] = useState(new Set());
    const [optimisticProgress, setOptimisticProgress] = useState(new Map());
    const [updatingProgress, setUpdatingProgress] = useState(new Set());
    const [optimisticScores, setOptimisticScores] = useState(new Map());
    const [updatingScores, setUpdatingScores] = useState(new Set());

    // State for pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [loadedPages, setLoadedPages] = useState(new Set([1]));
    const ITEMS_PER_PAGE = 15;

    // Save active tab to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem(OVERVIEW_TAB_STORAGE_KEY, activeTab);
    }, [activeTab]);

    // Save sort options to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem(OVERVIEW_SORT_BY_KEY, sortBy);
    }, [sortBy]);

    useEffect(() => {
        localStorage.setItem(OVERVIEW_SORT_DIR_KEY, sortDirection);
    }, [sortDirection]);

    // Filter and sort groups
    const filteredGroups = useMemo(() => {
        let groups = filterGroupsByStatus(animeGroups, activeTab);

        // Search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            const queryNoSpaces = query.replace(/\s+/g, '');
            groups = groups.filter(group => {
                const groupNameMatches = group.name.toLowerCase().includes(query) || group.name.toLowerCase().replace(/\s+/g, '').includes(queryNoSpaces);
                const matchingAnime = group.anime.filter(entry => {
                    const englishTitle = entry.media.title.english || '';
                    const romanjiTitle = entry.media.title.romaji || '';
                    return englishTitle.toLowerCase().includes(query) ||
                        romanjiTitle.toLowerCase().includes(query) ||
                        englishTitle.toLowerCase().replace(/\s+/g, '').includes(queryNoSpaces) ||
                        romanjiTitle.toLowerCase().replace(/\s+/g, '').includes(queryNoSpaces);
                });
                return groupNameMatches || matchingAnime.length > 0;
            });
        }

        // Apply sorting
        const sortOption = SORT_OPTIONS.find(opt => opt.id === sortBy);
        if (sortOption) {
            const direction = sortDirection === 'asc' ? 1 : -1;
            groups = [...groups].sort((a, b) => sortOption.sortFn(a, b, direction));
        }

        return groups;
    }, [animeGroups, activeTab, searchQuery, sortBy, sortDirection, titlePreference]);

    // Calculate paginated groups to display
    const displayedGroups = useMemo(() => {
        const groupsToShow = [];

        // Add groups from all loaded pages
        Array.from(loadedPages).sort((a, b) => a - b).forEach(page => {
            const startIndex = (page - 1) * ITEMS_PER_PAGE;
            const endIndex = startIndex + ITEMS_PER_PAGE;
            groupsToShow.push(...filteredGroups.slice(startIndex, endIndex));
        });

        return groupsToShow;
    }, [filteredGroups, loadedPages]);

    // Calculate total pages
    const totalPages = Math.ceil(filteredGroups.length / ITEMS_PER_PAGE);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
        setLoadedPages(new Set([1]));
    }, [activeTab, searchQuery, sortBy, sortDirection]);

    // BUFFERED PAGINATION: Immediately load Page 2 if available
    useEffect(() => {
        if (currentPage === 1 && totalPages > 1 && !loadedPages.has(2)) {
            // Use a small timeout to let the first page render first (optional, but good for perceived performance)
            const timer = setTimeout(() => {
                setLoadedPages(prev => new Set([...prev, 2]));
                setCurrentPage(2);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [currentPage, totalPages, loadedPages]);

    // Load next page
    const loadNextPage = React.useCallback(() => {
        const nextPage = currentPage + 1;
        if (nextPage <= totalPages && !loadedPages.has(nextPage)) {
            setLoadedPages(prev => new Set([...prev, nextPage]));
            setCurrentPage(nextPage);
        }
    }, [currentPage, totalPages, loadedPages]);

    // Infinite scroll handler
    const handleScroll = React.useCallback(() => {
        const scrollPosition = window.innerHeight + window.scrollY;
        const pageHeight = document.documentElement.scrollHeight;
        const triggerHeight = pageHeight - 1000; // Load aggressively (1000px before bottom) to maintain buffer

        if (scrollPosition >= triggerHeight && currentPage < totalPages && !loadedPages.has(currentPage + 1)) {
            loadNextPage();
        }
    }, [currentPage, totalPages, loadedPages, loadNextPage]);

    // Attach scroll listener
    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // Calculate counts for tabs
    const tabCounts = useMemo(() => {
        const counts = {};
        STATUS_TABS.forEach(tab => {
            counts[tab.id] = filterGroupsByStatus(animeGroups, tab.value).length;
        });
        return counts;
    }, [animeGroups, titlePreference]);

    const handleGroupClick = (groupId) => {
        const group = animeGroups.find(g => g.id === groupId);
        if (group) {
            setSelectedGroup(group);
        }
    };

    // Handle anime status change with optimistic updates (exactly like Collections)
    const handleStatusChange = async (mediaId, currentStatus, newStatus, animeTitle) => {
        if (newStatus === currentStatus) return;

        if (!token) {
            showError('Authentication required. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        // Optimistic update - update UI immediately
        setOptimisticUpdates(prev => new Map(prev).set(mediaId, newStatus));
        setChangingStatus(prev => new Set([...prev, mediaId]));

        // Add to undo history
        addChange({
            type: 'STATUS_CHANGE',
            mediaId,
            animeTitle,
            oldStatus: currentStatus,
            newStatus: newStatus,
            timestamp: Date.now()
        });

        try {
            await updateAnimeStatus(mediaId, newStatus, token);
            console.log(`✅ Updated ${animeTitle} to ${newStatus}`);
            // Trigger FORCE refresh to get fresh data from AniList (not cache)
            if (refreshData) {
                refreshData(true);
            }
            setChangingStatus(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
        } catch (error) {
            console.error(`❌ Failed to update status for ${animeTitle}:`, error);

            // Revert optimistic update on error
            setOptimisticUpdates(prev => {
                const next = new Map(prev);
                next.delete(mediaId);
                return next;
            });

            setChangingStatus(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });

            showError(`Failed to update status: ${error.message}`, 'Status Update Failed');
        }
    };

    // Handle score change
    const handleScoreChange = async (mediaId, newScoreRaw, animeTitle) => {
        if (!token) {
            showError('Authentication required.', 'Auth Required');
            return;
        }

        setOptimisticScores(prev => new Map(prev).set(mediaId, newScoreRaw));
        setUpdatingScores(prev => new Set([...prev, mediaId]));

        try {
            await updateMediaScore(mediaId, newScoreRaw, token);
            if (refreshData) refreshData(true);
        } catch (error) {
            showError(`Failed to update score for ${animeTitle}`, 'Score Update Failed');
            setOptimisticScores(prev => { const n = new Map(prev); n.delete(mediaId); return n; });
        } finally {
            setUpdatingScores(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
        }
    };

    // Handle episode progress change
    const handleProgressChange = async (mediaId, newProgress, totalEpisodes, animeTitle, mediaStatus, currentUserStatus) => {
        // Clamp value: 0 to totalEpisodes (0 means unreleased = no progress allowed)
        let clamped = Math.max(0, newProgress);
        if (totalEpisodes > 0) {
            clamped = Math.min(clamped, totalEpisodes);
        } else if (totalEpisodes === 0) {
            clamped = 0; // Unreleased anime — can't have progress
        }

        if (!token) {
            showError('Authentication required. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        // Optimistic update
        setOptimisticProgress(prev => new Map(prev).set(mediaId, clamped));
        setUpdatingProgress(prev => new Set([...prev, mediaId]));

        try {
            await updateAnimeProgress(mediaId, clamped, token);
            console.log(`✅ Updated ${animeTitle} progress to ${clamped}`);
            if (refreshData) {
                refreshData(true);
            }

            // Auto-start: if progress > 0 and status is PLANNING, switch to CURRENT (Watching)
            if (clamped > 0 && currentUserStatus === 'PLANNING') {
                console.log(`▶️ Auto-starting ${animeTitle} — progress set to ${clamped}, switching to Watching`);
                handleStatusChange(mediaId, currentUserStatus, 'CURRENT', animeTitle);
            }

            // Auto-complete: if all episodes watched and media is finished, mark as COMPLETED
            if (clamped > 0 && totalEpisodes > 0 && clamped === totalEpisodes && mediaStatus === 'FINISHED' && currentUserStatus !== 'COMPLETED') {
                console.log(`🎉 Auto-completing ${animeTitle} — all ${totalEpisodes} episodes watched`);
                handleStatusChange(mediaId, currentUserStatus, 'COMPLETED', animeTitle);
            }
        } catch (error) {
            console.error(`❌ Failed to update progress for ${animeTitle}:`, error);
            showError(`Failed to update progress for ${animeTitle}`, 'Update Failed');
            // Revert optimistic update
            setOptimisticProgress(prev => {
                const next = new Map(prev);
                next.delete(mediaId);
                return next;
            });
        } finally {
            setUpdatingProgress(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
        }
    };

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (selectedGroup) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
            // Trigger a manual scroll check when modal closes to see if we need to load more
            handleScroll();
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [selectedGroup, handleScroll]);



    // Show skeleton during initial load (no groups to display yet)
    if (isLoading && (!animeGroups || animeGroups.length === 0)) {
        return <DashboardSkeleton />;
    }

    if (!animeGroups || animeGroups.length === 0) {
        return (
            <div className="overview-container">
                <div className="empty-state">
                    <div className="empty-icon">🏠</div>
                    <h3>No Groups Yet</h3>
                    <p>Your anime groups will appear here once loaded.</p>
                    <p>Make sure you're connected to AniList.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="overview-container">
            <div className="overview-header">
                {/* Compact Tip - Left side */}
                <div className="tip-compact">
                    <span className="tip-icon">{randomTip.icon}</span>
                    <div className="tip-content">
                        <span className="tip-label">Tip</span>
                        <span className="tip-text">{randomTip.text}</span>
                    </div>
                </div>

                {/* Header - Right side */}
                <div className="overview-header-content">
                    <h1>🏠 Overview</h1>
                    <p>Your anime collection at a glance</p>
                </div>
            </div>

            {/* Search and Sort Bar */}
            <div className="search-sort-container">
                <input
                    type="text"
                    className="search-input"
                    placeholder="🔍 Search groups or anime..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="sort-controls">
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="sort-select"
                        title="Sort by"
                    >
                        {SORT_OPTIONS.map(option => (
                            <option key={option.id} value={option.id}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                        className="sort-direction-btn"
                        title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                    >
                        {sortDirection === 'asc' ? '↑' : '↓'}
                    </button>
                </div>
            </div>

            {/* Status Tabs */}
            <div className="status-tabs">
                {STATUS_TABS.map(tab => (
                    <button
                        key={tab.id}
                        className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                        <span className="tab-count">{tabCounts[tab.id] || 0}</span>
                    </button>
                ))}
            </div>

            {/* Groups Grid */}
            <div className="overview-groups-grid">
                {displayedGroups.length === 0 ? (
                    <div className="empty-state">
                        <p>
                            No groups found for {STATUS_TABS.find(tab => tab.id === activeTab)?.label || 'this filter'}{searchQuery && ` matching "${searchQuery}"`}.
                        </p>
                        {searchQuery && (
                            <p style={{ fontSize: '0.85em', opacity: 0.7, marginTop: '4px' }}>Check the spelling or try different keywords</p>
                        )}
                    </div>
                ) : (
                    displayedGroups.map(group => {
                        const oldestAnime = group.anime[0];
                        const coverImage = oldestAnime?.media?.coverImage?.extraLarge || oldestAnime?.media?.coverImage?.large || oldestAnime?.media?.coverImage?.medium;
                        const groupName = getDisplayTitle(oldestAnime?.media, titlePreference);
                        const hasReleasing = group.anime.some(a => a.media?.status === 'RELEASING');

                        return (
                            <div
                                key={group.id}
                                className="overview-group-card"
                                onClick={() => handleGroupClick(group.id)}
                                role="button"
                                tabIndex={0}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        handleGroupClick(group.id);
                                    }
                                }}
                            >
                                {coverImage && (
                                    <img
                                        src={coverImage}
                                        alt={groupName}
                                        className="overview-cover"
                                    />
                                )}

                                <div className="overview-group-info">
                                    <h3 className="overview-group-name">{groupName}</h3>
                                    {hasReleasing && <span className="group-releasing-tag">📡 Airing</span>}

                                    <div className="overview-stats">
                                        <span className="stat-anime-count">
                                            📚 {group.stats.animeCount} anime
                                        </span>
                                        <span>•</span>
                                        <span className="stat-episodes">
                                            🎬 {formatEpisodeProgress(group.stats.watchedEpisodes, group.stats.totalEpisodes)} eps
                                        </span>
                                        {group.stats.averageScore > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-score">
                                                    ⭐ {(group.stats.averageScore / 10).toFixed(1)}
                                                </span>
                                            </>
                                        )}
                                    </div>

                                    <div className="overview-time-stats">
                                        {(group.stats.totalDuration - group.stats.watchedDuration) > 0 && (
                                            <div className="time-stat remaining">
                                                <span className="time-label">⏳ Remaining</span>
                                                <span className="time-value">
                                                    {formatDuration(group.stats.totalDuration - group.stats.watchedDuration, hoursPerDay)}
                                                </span>
                                            </div>
                                        )}
                                        {group.stats.watchedDuration > 0 && (
                                            <div className="time-stat watched">
                                                <span className="time-label">✅ Watched</span>
                                                <span className="time-value">
                                                    {formatDuration(group.stats.watchedDuration, hoursPerDay)}
                                                </span>
                                            </div>
                                        )}
                                        <div className="time-stat total">
                                            <span className="time-label">📺 Total</span>
                                            <span className="time-value">
                                                {formatDuration(group.stats.totalDuration, hoursPerDay)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Summary Footer */}
            <div className="overview-summary">
                <p>
                    Showing {filteredGroups.length} groups
                    {searchQuery && ` for "${searchQuery}"`}
                </p>
            </div>

            {/* Group Card Modal Overlay */}
            {selectedGroup && (
                <div className="group-modal-overlay" onClick={() => setSelectedGroup(null)}>
                    <div className="group-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="close-btn" onClick={() => setSelectedGroup(null)}>×</button>

                        {/* Group Card */}
                        <div className="group-card-modal">
                            {/* Group Header */}
                            <div className="modal-group-header">
                                {selectedGroup.anime[0]?.media?.coverImage?.large && (
                                    <img
                                        src={selectedGroup.anime[0].media.coverImage.large}
                                        alt={getDisplayTitle(selectedGroup.anime[0]?.media, titlePreference)}
                                        className="modal-group-cover"
                                    />
                                )}
                                <div className="modal-group-info">
                                    <h2 className="modal-group-name">
                                        📚 {getDisplayTitle(selectedGroup.anime[0]?.media, titlePreference)}
                                    </h2>
                                    <div className="modal-group-stats">
                                        <span className="stat-anime-count">{selectedGroup.stats.animeCount} anime</span>
                                        <span>•</span>
                                        <span className="stat-episodes">
                                            {formatEpisodeProgress(selectedGroup.stats.watchedEpisodes, selectedGroup.stats.totalEpisodes)} eps
                                        </span>
                                        {selectedGroup.stats.averageScore > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-score">⭐ {(selectedGroup.stats.averageScore / 10).toFixed(1)}</span>
                                            </>
                                        )}
                                        <span>•</span>
                                        <span className="stat-total-time">📺 {formatDuration(selectedGroup.stats.totalDuration, hoursPerDay)}</span>
                                        {selectedGroup.stats.watchedDuration > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-watched-time">✅ {formatDuration(selectedGroup.stats.watchedDuration, hoursPerDay)}</span>
                                            </>
                                        )}
                                        {(selectedGroup.stats.totalDuration - selectedGroup.stats.watchedDuration) > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-remaining-time">⏳ {formatDuration(selectedGroup.stats.totalDuration - selectedGroup.stats.watchedDuration, hoursPerDay)}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Anime List */}
                            <div className="modal-anime-list">
                                {selectedGroup.anime.map((entry, index) => {
                                    const title = getDisplayTitle(entry.media, titlePreference);
                                    const releaseDate = formatReleaseDate(entry.media.startDate);
                                    const totalEpisodes = getTotalEpisodes(entry);
                                    const watchedEpisodes = entry.progress || 0;
                                    const animeTime = calculateAnimeTime(entry);
                                    // Get optimistic status if available, otherwise use actual status
                                    const displayStatus = optimisticUpdates.get(entry.media.id) || entry.status;

                                    return (
                                        <div key={entry.id} className="modal-anime-item">

                                            <img
                                                src={entry.media.coverImage?.extraLarge || entry.media.coverImage?.large || entry.media.coverImage?.medium}
                                                alt={title}
                                                className="anime-cover"
                                            />
                                            <div className="anime-details">
                                                <div className="anime-title-row">
                                                    <h4 className="anime-title">
                                                        <a
                                                            href={`https://anilist.co/anime/${entry.media.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {title}
                                                        </a>
                                                    </h4>
                                                    <div className="anime-action-buttons">
                                                        <ScoreInput
                                                            mediaId={entry.media.id}
                                                            currentScore={optimisticScores.has(entry.media.id) ? optimisticScores.get(entry.media.id) : (entry.score || 0)}
                                                            isUpdating={updatingScores.has(entry.media.id)}
                                                            onChange={(newScore) => handleScoreChange(entry.media.id, newScore, title)}
                                                        />
                                                        {/* Episode Counter */}
                                                        <EpisodeCounter
                                                            mediaId={entry.media.id}
                                                            currentProgress={optimisticProgress.has(entry.media.id) ? optimisticProgress.get(entry.media.id) : watchedEpisodes}
                                                            totalEpisodes={totalEpisodes}
                                                            isUpdating={updatingProgress.has(entry.media.id)}
                                                            onChange={(newVal) => handleProgressChange(entry.media.id, newVal, totalEpisodes, title, entry.media.status, displayStatus)}
                                                        />
                                                        <StatusButton
                                                            currentStatus={displayStatus}
                                                            onChange={(newStatus) => handleStatusChange(entry.media.id, displayStatus, newStatus, title)}
                                                            disabled={changingStatus.has(entry.media.id)}
                                                            isLoading={changingStatus.has(entry.media.id)}
                                                        />
                                                        <button
                                                            className="action-btn watch-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedAnimeForModal(title);
                                                                setShowWatchModal(true);
                                                            }}
                                                            title="Watch"
                                                        >
                                                            🎬
                                                        </button>

                                                    </div>
                                                </div>
                                                <div className="anime-meta">
                                                    <span>{releaseDate}</span>
                                                    <span>{entry.media.format}</span>
                                                    <span>{watchedEpisodes}/{totalEpisodes} eps</span>
                                                    {entry.score > 0 && <span>⭐ {(entry.score / 10).toFixed(1)}</span>}
                                                    {entry.media.averageScore > 0 && <span>📊 {(entry.media.averageScore / 10).toFixed(1)}</span>}
                                                    <span>📺 {formatDuration(animeTime.total, hoursPerDay)}</span>
                                                    {animeTime.watched > 0 && (
                                                        <span>✅ {formatDuration(animeTime.watched, hoursPerDay)}</span>
                                                    )}
                                                    {animeTime.remaining > 0 && (
                                                        <span>⏳ {formatDuration(animeTime.remaining, hoursPerDay)}</span>
                                                    )}
                                                    {(() => { const badge = getMediaStatusBadge(entry.media?.status); return <span className={`media-status-badge ${badge.className}`}>{badge.emoji} {badge.label}</span>; })()}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Site Links Modals */}
            <SiteLinksModal
                isOpen={showWatchModal}
                onClose={() => {
                    setShowWatchModal(false);
                    setSelectedAnimeForModal('');
                }}
                links={watchLinks}
                animeName={selectedAnimeForModal || ''}
                type="watch"
            />

        </div>
    );
};

export default Dashboard;
